// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  SheetScope,
  fetchSheet,
  getViewer,
  colorForEmail,
} from "@/lib/liveSheets";

export interface PresenceUser {
  name: string;
  email: string;
  color: string;
}

export function useLiveSheet(scope: SheetScope, token: string | undefined) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const presenceRef = useRef<any>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const result = await fetchSheet(scope, token);
      if (result?.error) {
        setError(result.error);
        setData(null);
      } else {
        setError(null);
        setData(result);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load sheet");
    } finally {
      setLoading(false);
    }
  }, [scope, token]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Determine gate requirement
  const allowedEmails: string[] | null = data?.allowed_emails ?? null;
  const needsGate = useMemo(() => {
    if (!data || error) return false;
    if (!allowedEmails || allowedEmails.length === 0) return false;
    if (gatePassed) return false;
    const { email } = getViewer();
    if (email && allowedEmails.map((e) => e.toLowerCase()).includes(email.toLowerCase())) {
      return false; // auto-skip
    }
    return true;
  }, [data, error, allowedEmails, gatePassed]);

  const eventId = data?.event?.id;

  // Realtime refetch on registration changes
  useEffect(() => {
    if (!eventId || scope === "roster") return;
    const channel = supabase
      .channel(`sheet-realtime-${eventId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_registrations_v2", filter: `event_id=eq.${eventId}` },
        () => load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, scope, load]);

  // Roster realtime
  useEffect(() => {
    if (!eventId || scope !== "roster") return;
    const channel = supabase
      .channel(`sheet-realtime-roster-${eventId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_rosters", filter: `event_id=eq.${eventId}` },
        () => load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, scope, load]);

  // Presence
  useEffect(() => {
    if (!token || !data || needsGate) return;
    const { name, email } = getViewer();
    const viewerEmail = email || `anon-${Math.random().toString(36).slice(2, 8)}`;
    const me: PresenceUser = {
      name: name || "Guest",
      email: viewerEmail,
      color: colorForEmail(viewerEmail),
    };
    const channel = supabase.channel(`sheet-presence-${token}`, {
      config: { presence: { key: viewerEmail } },
    });
    presenceRef.current = channel;
    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const users: PresenceUser[] = [];
        Object.values(state).forEach((arr: any) => {
          if (arr[0]) users.push(arr[0]);
        });
        setPresence(users);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") await channel.track(me);
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [token, data, needsGate]);

  return {
    data,
    loading,
    error,
    needsGate,
    onGatePassed: () => setGatePassed(true),
    refetch: load,
    presence,
    permission: data?.permission as "view" | "comment" | "edit" | undefined,
    allowedEmails,
  };
}
